/**
 * Component: SearchPagination
 * SpecReferenceID: SEARCH-SEMANTIC-V1
 * Requirements: SEARCH-SEMANTIC-V1-R03, FRONTEND-WEB-V1-R13, FRONTEND-WEB-V1-R14,
 *               FRONTEND-WEB-V1-R15
 *
 * Reusable pagination controls for search result pages.
 * Displays page navigation when total results exceed the page size.
 *
 * SEARCH-SEMANTIC-V1-R03: Pagination of result presentation.
 * FRONTEND-WEB-V1-R13: Responsive design (mobile/desktop).
 * FRONTEND-WEB-V1-R14: WCAG 2.1 AA accessible.
 * FRONTEND-WEB-V1-R15: Full keyboard navigation.
 *
 * Accessibility: WCAG 2.1 AA compliant
 */

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SearchPaginationProps {
  currentPage: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean;
}

export function SearchPagination({
  currentPage,
  totalCount,
  pageSize,
  onPageChange,
  isLoading = false,
}: SearchPaginationProps): React.ReactElement | null {
  const totalPages = Math.ceil(totalCount / pageSize);

  if (totalPages <= 1) {
    return null;
  }

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  /**
   * Generate page numbers to display.
   * Shows first, last, current, and adjacent pages with ellipses.
   */
  const getPageNumbers = (): (number | 'ellipsis')[] => {
    const pages: (number | 'ellipsis')[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    if (currentPage > 3) {
      pages.push('ellipsis');
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 2) {
      pages.push('ellipsis');
    }

    pages.push(totalPages);

    return pages;
  };

  return (
    <nav
      aria-label="Search results pagination"
      className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-between"
    >
      <p className="text-sm text-muted-foreground" aria-live="polite">
        Showing {startItem}–{endItem} of {totalCount} results
      </p>
      <div className="flex items-center gap-1" role="group" aria-label="Page navigation">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1 || isLoading}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          <span className="sr-only sm:not-sr-only sm:ml-1">Previous</span>
        </Button>

        {getPageNumbers().map((page, index) =>
          page === 'ellipsis' ? (
            <span
              key={`ellipsis-${index}`}
              className="px-2 text-sm text-muted-foreground"
              aria-hidden="true"
            >
              ...
            </span>
          ) : (
            <Button
              key={page}
              variant={page === currentPage ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(page)}
              disabled={isLoading}
              aria-label={`Page ${page}`}
              aria-current={page === currentPage ? 'page' : undefined}
              className="min-w-[2.25rem]"
            >
              {page}
            </Button>
          )
        )}

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages || isLoading}
          aria-label="Next page"
        >
          <span className="sr-only sm:not-sr-only sm:mr-1">Next</span>
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>
    </nav>
  );
}
